import { useQuery } from "@tanstack/react-query";
import { kebabCase } from "lodash";
import { useCallback } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";
import Palette from "@/Utils/Colors/palette";
import { getMappedName } from "@/Utils/mapping-utils";

import { CurveType } from "../constants/maps";
import { calculateMarginalROI } from "../helpers/calculateMarginalROI";
import { calculateMaxEfficiency } from "../helpers/calculateMaxEfficiency";
import { calculateYieldCurve } from "../helpers/calculateYieldCurve";
import { generateMarkData } from "../helpers/createMarkCoordinates";
import { CurveAnalyser } from "../helpers/curveAnalysis";
import { ComparisonMode } from "../types/comparisonMode";
import { SaturationCurvesDTO } from "../types/dto";
import { Rating, SaturationCurve } from "../types/saturationCurve";

const getSaturationCurvesV2 = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  geography,
  channels,
  comparisonMode,
}: {
  brand: string;
  category: string;
  product: string;
  startDate: string | null;
  endDate: string | null;
  geography?: string;
  channels: string[];
  comparisonMode?: ComparisonMode;
}): Promise<SaturationCurvesDTO> => {
  return axiosApi.get("/api/v2/investment-levels", {
    baseURL: INSIGHTS_BACKEND_URL,
    params: {
      brand,
      category,
      product,
      startDate,
      endDate,
      geography,
      channels,
      comparisonMode,
    },
  });
};

const getSaturationCurvesNest = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  geography,
  channels,
  comparisonMode,
}: {
  brand: string;
  category: string;
  product: string;
  startDate: string | null;
  endDate: string | null;
  geography?: string;
  channels: string[];
  comparisonMode?: ComparisonMode;
}): Promise<SaturationCurvesDTO> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (geography) params.append("geography", geography);
  if (comparisonMode) params.append("comparisonMode", comparisonMode);

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  return axiosApi.get(`/api/investment-levels?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

type QueryFnType = typeof getSaturationCurvesV2;

type UseSaturationCurvesOptions = {
  brand: string;
  category: string;
  product: string;
  startDate: string | null;
  endDate: string | null;
  palette: Palette;
  geography?: string;
  channels: string[];
  comparisonMode?: ComparisonMode;
  config?: QueryConfig<QueryFnType>;
};

export const useSaturationCurves = ({
  palette,
  brand,
  category,
  product,
  startDate,
  endDate,
  geography,
  channels,
  config,
  comparisonMode,
}: UseSaturationCurvesOptions) => {
  const endpointType = useModuleEndpoint("investment-levels");

  if (endpointType === "new") {
    return useQuery({
      ...config,
      queryKey: [
        "investment-levels",
        brand,
        category,
        product,
        startDate,
        endDate,
        geography,
        channels,
        comparisonMode,
      ],
      queryFn: () =>
        getSaturationCurvesNest({
          brand,
          category,
          product,
          startDate,
          endDate,
          geography,
          channels,
          comparisonMode,
        }),
      select: useCallback((data: SaturationCurvesDTO) => selectSaturationCurves(data, palette), []),
    });
  }
  return useQuery({
    ...config,
    queryKey: [
      "investment-levels",
      brand,
      category,
      product,
      startDate,
      endDate,
      geography,
      channels,
      comparisonMode,
    ],
    queryFn: () =>
      getSaturationCurvesV2({
        brand,
        category,
        product,
        startDate,
        endDate,
        geography,
        channels,
        comparisonMode,
      }),
    select: useCallback((data: SaturationCurvesDTO) => selectSaturationCurves(data, palette), []),
  });
};

const selectSaturationCurves = (data: SaturationCurvesDTO, palette: Palette): SaturationCurve[] => {
  return data.map((curve) => {
    const uniqueId = kebabCase(
      ` ${curve.brand} ${curve.product} ${curve.category} ${curve.geography} ${curve.channel}`
    );
    const curveCoordinates: [number, number][] = curve.data.curve_data ?? [];

    const yieldCoordinates = calculateYieldCurve(curveCoordinates);
    const marginalROICoordinates = calculateMarginalROI(curveCoordinates);

    const revenueAnalyser = new CurveAnalyser(curveCoordinates);
    const yieldAnalyser = new CurveAnalyser(yieldCoordinates);
    const marginalROIAnalyser = new CurveAnalyser(marginalROICoordinates);

    const maxEfficiency = revenueAnalyser.applyToCoordinates(calculateMaxEfficiency()) || 0;

    const { lower_bound, upper_bound, curve_value } = curve.data;

    const pointsConfig = {
      currentSpend: curve_value ?? 0,
      maxEfficiency: maxEfficiency,
      lowerThreshold: lower_bound ?? 0,
      upperThreshold: upper_bound ?? 0,
    };

    const curveTypes = {
      [CurveType.REVENUE]: {
        curveType: CurveType.REVENUE,
        curveCoordinates: curveCoordinates,
        markData: generateMarkData(revenueAnalyser.mapTargetPointsToCoordinates(pointsConfig)),
      },
      [CurveType.MARGINAL_ROI]: {
        curveType: CurveType.MARGINAL_ROI,
        curveCoordinates: marginalROICoordinates,
        markData: generateMarkData(marginalROIAnalyser.mapTargetPointsToCoordinates(pointsConfig)),
      },
      [CurveType.YIELD]: {
        curveType: CurveType.YIELD,
        curveCoordinates: yieldCoordinates,
        markData: generateMarkData(yieldAnalyser.mapTargetPointsToCoordinates(pointsConfig)),
      },
    };

    return {
      id: uniqueId,
      color: palette.chooseColorMemo(uniqueId).color,
      // Secondary color is used for geography comparison
      secondaryColor: palette.chooseColor(uniqueId).color,
      nodeUUID: curve.node_uuid,
      channel: curve.channel,
      channelLabel: getMappedName(curve.channel) ?? curve.channel,
      geography: curve.geography,
      product: curve.product,
      category: curve.category,
      brand: curve.brand,
      activeWeeks: curve.data.active_weeks ?? 0,
      rating: curve.data.rating as Rating | null,
      revenue: curve.data.return_val ?? 0,
      spend: curve.data.spend ?? 0,
      confidence: curve.data.confidence ?? 0,
      lowerBound: curve.data.lower_bound ?? 0,
      upperBound: curve.data.upper_bound ?? 0,
      curveValue: curve.data.curve_value ?? 0,
      curveTypes,
      maxEfficiency: maxEfficiency,
      roi: curve.data.roi ?? 0,
      roas: curve.data.roas ?? 0,
      units: curve.data.units ?? 0,
    };
  });
};
